/** Client-only menu sticky behavior (DOM). Re-exports SSR helpers from `-shared`. */

export * from './header-menu-sticky-shared';

import { applyNavBarBackgroundToMount } from './nav-bar-background';
import {
  isHeaderChromeStickyEnabled,
  measureHeaderChromeOffsetTop,
  syncHeaderChromeHeightVar,
} from './header-chrome-sticky';
import {
  headerDesktopNavClassName,
  headerNavBarShellClassName,
  headerNavbarClassName,
  resolveHeaderMenuSticky,
} from './header-menu-sticky-shared';

const SSR_HEADER_SELECTOR = '[data-ssr-header="true"]';
const DESKTOP_NAV_SELECTOR = 'nav.navbar-nav.hide-mobile';
const NAV_BAR_SELECTOR = '[data-storefront-nav-bar="true"]';
const LEGACY_CHROME_SELECTOR = '[data-storefront-header-chrome="true"]';
const SPACER_SELECTOR = '[data-header-menu-sticky-spacer="true"]';
const BAR_FIXED_CLASS = 'storefront-nav-bar--fixed';

type MountStickyState = {
  scroll: () => void;
  resize: () => void;
  chromeChange: () => void;
};

const mountStickyStates = new WeakMap<HTMLElement, MountStickyState>();

function clearPositionStyles(element: HTMLElement): void {
  element.style.removeProperty('position');
  element.style.removeProperty('top');
  element.style.removeProperty('left');
  element.style.removeProperty('right');
  element.style.removeProperty('width');
  element.style.removeProperty('z-index');
  element.style.removeProperty('min-height');
}

/** Legacy wrapper wrapped top bar + menu; only the menu may be sticky. */
export function unwrapHeaderChromeIfPresent(headerMount: HTMLElement): void {
  const chrome = headerMount.querySelector(LEGACY_CHROME_SELECTOR);
  if (!(chrome instanceof HTMLElement)) {
    return;
  }

  const fragment = document.createDocumentFragment();
  while (chrome.firstChild) {
    fragment.appendChild(chrome.firstChild);
  }
  headerMount.insertBefore(fragment, chrome);
  chrome.remove();
  clearPositionStyles(headerMount);
}

function findDesktopNav(headerMount: HTMLElement): HTMLElement | null {
  const header = headerMount.querySelector(SSR_HEADER_SELECTOR);
  const inHeader = header?.querySelector(DESKTOP_NAV_SELECTOR);
  if (inHeader instanceof HTMLElement) {
    return inHeader;
  }
  const direct = headerMount.querySelector(DESKTOP_NAV_SELECTOR);
  return direct instanceof HTMLElement ? direct : null;
}

/**
 * Full-width menu shell (like storefront-top-bar) with inner .container for links.
 */
export function ensureNavBarShell(nav: HTMLElement): HTMLElement {
  const existing = nav.closest(NAV_BAR_SELECTOR);
  if (existing instanceof HTMLElement) {
    return existing;
  }

  const bar = document.createElement('div');
  bar.className = headerNavBarShellClassName();
  bar.dataset.storefrontNavBar = 'true';

  const inner = document.createElement('div');
  inner.className = 'container storefront-nav-bar__inner';

  const container = nav.closest('.container');
  if (container instanceof HTMLElement && nav.parentElement === container) {
    container.insertAdjacentElement('afterend', bar);
  } else if (nav.parentElement instanceof HTMLElement) {
    nav.parentElement.insertBefore(bar, nav);
    bar.appendChild(inner);
    inner.appendChild(nav);
    return bar;
  } else {
    nav.parentElement?.insertBefore(bar, nav);
  }

  inner.appendChild(nav);
  bar.appendChild(inner);
  return bar;
}

function getOrCreateSpacer(bar: HTMLElement): HTMLElement {
  const existing = bar.parentElement?.querySelector(SPACER_SELECTOR);
  if (existing instanceof HTMLElement) {
    return existing;
  }
  const spacer = document.createElement('div');
  spacer.setAttribute('data-header-menu-sticky-spacer', 'true');
  spacer.hidden = true;
  spacer.setAttribute('aria-hidden', 'true');
  bar.insertAdjacentElement('afterend', spacer);
  return spacer;
}

function detachScrollSticky(headerMount: HTMLElement): void {
  const state = mountStickyStates.get(headerMount);
  if (!state) {
    return;
  }
  window.removeEventListener('scroll', state.scroll);
  window.removeEventListener('resize', state.resize);
  window.removeEventListener('pagayo:header-chrome-sticky-change', state.chromeChange);
  mountStickyStates.delete(headerMount);
}

function releaseFixedBar(bar: HTMLElement, headerMount: HTMLElement): void {
  bar.classList.remove(BAR_FIXED_CLASS);
  clearPositionStyles(bar);
  const spacer = headerMount.querySelector(SPACER_SELECTOR);
  if (spacer instanceof HTMLElement) {
    spacer.hidden = true;
    spacer.style.height = '';
  }
}

/** When chrome is sticky too, fix the menu at the same scroll point as chrome (not later). */
function resolveMenuFixScrollThreshold(
  headerMount: HTMLElement,
  barOffsetTop: number,
  chromeOffsetTop: number | null,
): number {
  if (!isHeaderChromeStickyEnabled(headerMount)) {
    return barOffsetTop;
  }
  const chromeTop =
    chromeOffsetTop ?? measureHeaderChromeOffsetTop(headerMount);
  if (chromeTop === null) {
    return barOffsetTop;
  }
  return Math.min(barOffsetTop, chromeTop);
}

function attachScrollSticky(headerMount: HTMLElement, bar: HTMLElement): void {
  detachScrollSticky(headerMount);
  const spacer = getOrCreateSpacer(bar);
  let barOffsetTop: number | null = null;
  let chromeOffsetTop: number | null = null;

  const measureBarOffsetTop = (): number => {
    return bar.getBoundingClientRect().top + window.scrollY;
  };

  const update = (): void => {
    if (!bar.isConnected || !headerMount.isConnected) {
      detachScrollSticky(headerMount);
      return;
    }

    if (barOffsetTop === null) {
      barOffsetTop = measureBarOffsetTop();
    }

    if (chromeOffsetTop === null && isHeaderChromeStickyEnabled(headerMount)) {
      chromeOffsetTop = measureHeaderChromeOffsetTop(headerMount);
    }

    const fixThreshold = resolveMenuFixScrollThreshold(
      headerMount,
      barOffsetTop,
      chromeOffsetTop,
    );
    const shouldFix = window.scrollY >= fixThreshold - 0.5;

    if (shouldFix) {
      const height = bar.offsetHeight;
      spacer.style.height = `${height}px`;
      spacer.hidden = false;
      bar.classList.add(BAR_FIXED_CLASS);
      bar.style.removeProperty('top');
      if (isHeaderChromeStickyEnabled(headerMount)) {
        syncHeaderChromeHeightVar(headerMount);
      }
      return;
    }

    releaseFixedBar(bar, headerMount);
  };

  const onScroll = (): void => {
    update();
  };

  const onResize = (): void => {
    barOffsetTop = null;
    chromeOffsetTop = null;
    syncHeaderChromeHeightVar(headerMount);
    update();
  };

  const onChromeStickyChange = (): void => {
    syncHeaderChromeHeightVar(headerMount);
    update();
  };

  mountStickyStates.set(headerMount, {
    scroll: onScroll,
    resize: onResize,
    chromeChange: onChromeStickyChange,
  });
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('pagayo:header-chrome-sticky-change', onChromeStickyChange);
  update();
}

/**
 * Sticky desktop menu bar (full-width shell). Top bar + logo/search scroll away.
 */
export function applyHeaderMenuStickyToMount(
  headerMount: HTMLElement,
  menuSticky: boolean,
  headerNavBarBackgroundColor?: unknown,
  headerSubmenuBackgroundColor?: unknown,
  headerMenuHoverBackgroundColor?: unknown,
  headerSubmenuHoverBackgroundColor?: unknown,
): void {
  unwrapHeaderChromeIfPresent(headerMount);

  const sticky = resolveHeaderMenuSticky(menuSticky);
  const header = headerMount.querySelector(SSR_HEADER_SELECTOR);
  const nav = findDesktopNav(headerMount);

  if (header instanceof HTMLElement) {
    header.className = headerNavbarClassName();
    header.dataset.headerMenuSticky = sticky ? 'true' : 'false';
    clearPositionStyles(header);
  }

  headerMount.dataset.headerMenuSticky = sticky ? 'true' : 'false';
  headerMount.style.removeProperty('min-height');

  detachScrollSticky(headerMount);

  if (!(nav instanceof HTMLElement)) {
    return;
  }

  nav.className = headerDesktopNavClassName();
  const bar = ensureNavBarShell(nav);
  bar.className = headerNavBarShellClassName();
  bar.dataset.headerMenuSticky = sticky ? 'true' : 'false';

  applyNavBarBackgroundToMount(
    headerMount,
    headerNavBarBackgroundColor,
    headerSubmenuBackgroundColor,
    headerMenuHoverBackgroundColor,
    headerSubmenuHoverBackgroundColor,
  );

  if (!sticky) {
    releaseFixedBar(bar, headerMount);
    return;
  }

  attachScrollSticky(headerMount, bar);
}
